import { getPercentageOfLevelFromXP, getXPForLevel } from '@blind-kings/game-core';
import { FiCamera, FiEdit } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import ProfileAvatar from '../../Components/ProfileAvatar';
import { getPlayerSkinDef, skinSwatchBackground } from '../../player-colors';
import { tierLabel } from '../../profile-frames/registry';
import type { ProfileData } from './Profile';

export type ProfileHeroProps = {
  profile: ProfileData;
  isOwnProfile: boolean;
  inMatch?: boolean;
  /** Full hero on overview; compact strip on collection tabs. */
  compact?: boolean;
  onEditName?: () => void;
  onChangeAvatar?: () => void;
};

export default function ProfileHero({
  profile,
  isOwnProfile,
  inMatch = false,
  compact = false,
  onEditName,
  onChangeAvatar,
}: ProfileHeroProps) {
  const {
    displayName = 'Player',
    level = 1,
    xp = 0,
    wins = 0,
    losses = 0,
    gamesPlayed = 0,
    avatarId,
    equippedColorId,
  } = profile;

  const skin = getPlayerSkinDef(
    typeof equippedColorId === 'string' ? equippedColorId : undefined
  );
  const banner = skinSwatchBackground(skin);
  const levelStartXp = getXPForLevel(Number(level));
  const nextLevelXp = getXPForLevel(Number(level) + 1);
  const xpIntoLevel = Math.max(0, Number(xp) - levelStartXp);
  const xpForNextLevel = Math.max(1, nextLevelXp - levelStartXp);
  const xpPercent = Math.min(
    100,
    Math.max(0, getPercentageOfLevelFromXP(Number(level), Number(xp)))
  );

  return (
    <header className="relative border-b border-bk-gold-dim/40">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background: `linear-gradient(120deg, ${banner} 0%, transparent 55%), radial-gradient(ellipse at 20% 0%, color-mix(in srgb, var(--color-bk-gold) 18%, transparent), transparent 60%)`,
        }}
        aria-hidden
      />
      <div className="bk-stage-bg pointer-events-none absolute inset-0 opacity-80" aria-hidden />

      <div
        className={[
          'relative mx-auto flex w-full max-w-6xl items-center gap-4 px-4 sm:gap-6 sm:px-6 lg:px-10',
          compact ? 'py-4 sm:py-5' : 'flex-col gap-6 py-8 sm:flex-row sm:items-end sm:py-10',
        ].join(' ')}
      >
        <div className={['relative shrink-0', compact ? '' : 'mx-auto sm:mx-0'].join(' ')}>
          {/* Reserve space under the avatar for the overlapping level badge */}
          <div className={compact ? 'pb-3' : 'pb-5'}>
            <div className="relative">
              <ProfileAvatar
                avatarId={avatarId as string | undefined}
                frameId={
                  (profile.ranked?.displayFrameId as string | undefined) ??
                  undefined
                }
                size={compact ? 'md' : 'xl'}
              />
              <span
                className={[
                  'absolute left-1/2 flex -translate-x-1/2 items-center justify-center rounded-full border-2 border-bk-gold bg-bk-bg font-extrabold tabular-nums text-bk-text',
                  compact
                    ? 'bottom-0 h-6 min-w-6 translate-y-1/2 px-1.5 text-[10px]'
                    : 'bottom-0 h-8 min-w-8 translate-y-1/2 px-2 text-sm',
                ].join(' ')}
                title={`Level ${level}`}
              >
                {level}
              </span>
              {isOwnProfile && onChangeAvatar ? (
                <button
                  type="button"
                  onClick={onChangeAvatar}
                  className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center border border-bk-gold bg-bk-panel text-bk-gold transition-colors hover:bg-bk-gold hover:text-bk-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-bk-gold"
                  title="Change portrait"
                  aria-label="Change portrait"
                >
                  <FiCamera className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div
          className={[
            'min-w-0 flex-1 space-y-3',
            compact ? 'text-left' : 'text-center sm:pb-1 sm:text-left',
          ].join(' ')}
        >
          <div>
            <div
              className={[
                'flex flex-wrap items-center gap-2',
                compact ? 'justify-start' : 'justify-center sm:justify-start',
              ].join(' ')}
            >
              <h1
                className={[
                  'font-extrabold tracking-tight text-bk-text',
                  compact
                    ? 'text-xl sm:text-2xl'
                    : 'text-3xl sm:text-4xl md:text-5xl',
                ].join(' ')}
              >
                {displayName}
              </h1>
              {isOwnProfile && onEditName ? (
                <button
                  type="button"
                  className="text-bk-gold hover:text-bk-gold-bright"
                  aria-label="Edit display name"
                  onClick={onEditName}
                >
                  <FiEdit className="h-5 w-5" />
                </button>
              ) : null}
            </div>
            <div
              className={[
                'mt-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider',
                compact ? 'justify-start' : 'justify-center sm:justify-start',
              ].join(' ')}
            >
              <span
                className={`h-2 w-2 rounded-full ${
                  inMatch ? 'bg-bk-gold-bright' : 'bg-emerald-400'
                }`}
              />
              <span className={inMatch ? 'text-bk-gold' : 'text-emerald-400'}>
                {inMatch ? 'In Match' : 'Online'}
              </span>
            </div>
          </div>

          {!compact ? (
            <>
              <div className="mx-auto max-w-md space-y-1.5 sm:mx-0">
                <div className="flex justify-between text-[11px] uppercase tracking-wide text-bk-muted">
                  <span>XP</span>
                  <span>
                    {xpIntoLevel.toLocaleString()} /{' '}
                    {xpForNextLevel.toLocaleString()}
                  </span>
                </div>
                <div
                  className="h-2.5 w-full overflow-hidden border border-bk-gold-dim/50 bg-bk-bg"
                  role="progressbar"
                  aria-valuenow={Math.round(xpPercent)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Level ${level} experience`}
                >
                  <div
                    className="h-full bg-gradient-to-r from-bk-gold-dim to-bk-gold-bright"
                    style={{ width: `${xpPercent}%` }}
                  />
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
                {profile.ranked?.elo != null ? (
                  <Link
                    to="/leaderboard"
                    className="min-w-[4.5rem] border border-bk-gold bg-bk-gold/10 px-3 py-2 hover:bg-bk-gold/20"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-bk-gold">
                      {tierLabel(profile.ranked.tier as string | undefined)}
                    </div>
                    <div className="text-lg font-extrabold tabular-nums text-bk-text">
                      {String(profile.ranked.elo)}
                    </div>
                  </Link>
                ) : null}
                {(
                  [
                    ['Wins', wins],
                    ['Losses', losses],
                    ['Played', gamesPlayed],
                  ] as [string, unknown][]
                ).map(([label, value]) => (
                  <div
                    key={label}
                    className="min-w-[4.5rem] border border-bk-gold-dim/40 bg-bk-panel/80 px-3 py-2"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-bk-gold">
                      {label}
                    </div>
                    <div className="text-lg font-extrabold tabular-nums text-bk-text">
                      {String(value ?? 0)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}
